// src/pages/Pricing.js
import React from "react";
import { useNavigate } from "react-router-dom";
import SEO from "../components/SEO";
import { useUserTier } from "../context/UserTierContext";
import { useAuthModal } from "../context/AuthModalContext";

const plans = [
  {
    id: "free",
    name: "Free",
    price: null,
    priceLabel: "$0",
    period: "forever",
    cta: "Get Started",
    ctaVariant: "outline",
    highlight: false,
    features: [
      "25 Apex & SOQL challenges",
      "Learn pages & concept guides",
      "Basic LWC preview",
      "Community access",
    ],
    missing: [
      "Full challenge library (100+)",
      "View solutions",
      "Apex30 daily challenge",
      "Mock PD1 exam",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: 9,
    priceLabel: "$9",
    period: "/ month",
    lifetimeLabel: "$69 one-time",
    cta: "Upgrade to Pro",
    ctaVariant: "primary",
    highlight: true,
    badge: "Most Popular",
    features: [
      "Everything in Free",
      "Full challenge library (100+)",
      "View solutions after submission",
      "Apex30 daily challenge",
      "Mock PD1 exam",
      "Priority support",
    ],
    missing: [],
  },
  {
    id: "team",
    name: "Team",
    price: 49,
    priceLabel: "$49",
    period: "/ month",
    seats: "5 seats",
    cta: "Start Team Trial",
    ctaVariant: "outline",
    highlight: false,
    features: [
      "Everything in Pro",
      "5 team seats",
      "Team dashboard & progress tracking",
      "Admin controls",
      "Bulk billing",
    ],
    missing: [],
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { tier } = useUserTier();
  const { openAuthModal } = useAuthModal();

  function handleCTA(plan) {
    if (plan.id === "free") {
      if (tier === "guest") openAuthModal();
      else navigate("/challenges");
      return;
    }
    // Pro / Team — send to checkout (replace with Stripe link later)
    if (tier === "guest") {
      openAuthModal();
    } else {
      // TODO: replace with Stripe checkout URL
      alert(`Stripe checkout coming soon for ${plan.name}!`);
    }
  }

  return (
    <>
      <SEO
        title="Pricing — TrailCode"
        description="Choose the plan that fits your Salesforce learning journey. Free, Pro ($9/mo), or Team ($49/mo)."
      />

      <div style={styles.page}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>Simple, honest pricing</h1>
          <p style={styles.subtitle}>
            Start free. Upgrade when you're ready to go all‑in on your
            Salesforce cert.
          </p>
        </div>

        {/* Cards */}
        <div style={styles.grid}>
          {plans.map((plan) => (
            <div
              key={plan.id}
              style={{
                ...styles.card,
                ...(plan.highlight ? styles.cardHighlight : {}),
              }}
            >
              {plan.badge && <div style={styles.badge}>{plan.badge}</div>}

              <div style={styles.planName}>{plan.name}</div>

              <div style={styles.priceRow}>
                <span style={styles.price}>{plan.priceLabel}</span>
                <span style={styles.period}>{plan.period}</span>
              </div>

              {plan.lifetimeLabel && (
                <div style={styles.lifetime}>
                  or{" "}
                  <strong style={{ color: "#22d3ee" }}>
                    {plan.lifetimeLabel}
                  </strong>{" "}
                  lifetime
                </div>
              )}

              {plan.seats && (
                <div style={styles.seats}>{plan.seats}</div>
              )}

              <button
                style={{
                  ...styles.cta,
                  ...(plan.ctaVariant === "primary"
                    ? styles.ctaPrimary
                    : styles.ctaOutline),
                }}
                onClick={() => handleCTA(plan)}
              >
                {plan.cta}
              </button>

              <ul style={styles.featureList}>
                {plan.features.map((f) => (
                  <li key={f} style={styles.featureItem}>
                    <span style={styles.check}>✓</span> {f}
                  </li>
                ))}
                {plan.missing.map((f) => (
                  <li key={f} style={{ ...styles.featureItem, ...styles.missing }}>
                    <span style={styles.cross}>✕</span> {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* FAQ strip */}
        <div style={styles.faq}>
          <p>
            <strong>Can I cancel anytime?</strong> Yes — Pro is month-to-month,
            no contracts.
          </p>
          <p>
            <strong>What's the lifetime deal?</strong> Pay once ($69), keep Pro
            access forever — even as we add more challenges.
          </p>
          <p>
            <strong>Do you offer student discounts?</strong> Email us at{" "}
            <a href="mailto:hello@trailcode.org" style={styles.link}>
              hello@trailcode.org
            </a>
            .
          </p>
        </div>
      </div>
    </>
  );
}

/* ─── Inline styles ─────────────────────────────────────────────────────── */
const styles = {
  page: {
    minHeight: "100vh",
    background: "#0f172a",
    color: "#e2e8f0",
    padding: "64px 24px",
    fontFamily: "'Inter', sans-serif",
  },
  header: {
    textAlign: "center",
    marginBottom: 56,
  },
  title: {
    fontSize: 40,
    fontWeight: 700,
    color: "#f8fafc",
    margin: "0 0 12px",
  },
  subtitle: {
    fontSize: 18,
    color: "#94a3b8",
    margin: 0,
  },
  grid: {
    display: "flex",
    gap: 24,
    justifyContent: "center",
    flexWrap: "wrap",
    maxWidth: 1040,
    margin: "0 auto",
  },
  card: {
    background: "#1e293b",
    border: "1px solid #334155",
    borderRadius: 16,
    padding: "32px 28px",
    width: 300,
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  cardHighlight: {
    border: "2px solid #22d3ee",
    background: "#0f2942",
    boxShadow: "0 0 32px rgba(34,211,238,0.12)",
  },
  badge: {
    position: "absolute",
    top: -14,
    left: "50%",
    transform: "translateX(-50%)",
    background: "#22d3ee",
    color: "#0f172a",
    fontSize: 12,
    fontWeight: 700,
    padding: "4px 14px",
    borderRadius: 99,
    whiteSpace: "nowrap",
  },
  planName: {
    fontSize: 20,
    fontWeight: 600,
    color: "#f1f5f9",
    marginBottom: 4,
  },
  priceRow: {
    display: "flex",
    alignItems: "baseline",
    gap: 4,
    marginBottom: 4,
  },
  price: {
    fontSize: 40,
    fontWeight: 800,
    color: "#f8fafc",
  },
  period: {
    fontSize: 15,
    color: "#94a3b8",
  },
  lifetime: {
    fontSize: 13,
    color: "#94a3b8",
    marginBottom: 4,
  },
  seats: {
    fontSize: 13,
    color: "#22d3ee",
    fontWeight: 600,
    marginBottom: 4,
  },
  cta: {
    width: "100%",
    padding: "12px 0",
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    margin: "12px 0",
    transition: "opacity 0.15s",
  },
  ctaPrimary: {
    background: "#22d3ee",
    color: "#0f172a",
    border: "none",
  },
  ctaOutline: {
    background: "transparent",
    color: "#22d3ee",
    border: "2px solid #22d3ee",
  },
  featureList: {
    listStyle: "none",
    padding: 0,
    margin: "8px 0 0",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  featureItem: {
    fontSize: 14,
    color: "#cbd5e1",
    display: "flex",
    gap: 8,
    alignItems: "flex-start",
  },
  missing: {
    opacity: 0.4,
  },
  check: {
    color: "#22d3ee",
    fontWeight: 700,
    flexShrink: 0,
  },
  cross: {
    color: "#64748b",
    fontWeight: 700,
    flexShrink: 0,
  },
  faq: {
    maxWidth: 640,
    margin: "56px auto 0",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    color: "#94a3b8",
    fontSize: 14,
    lineHeight: 1.6,
  },
  link: {
    color: "#22d3ee",
    textDecoration: "none",
  },
};